import React, { useEffect, useMemo, useCallback, Suspense } from 'react'
import { useTranslation } from 'react-i18next'

import Theme from '@ublique/components/Theme'
import Loading from '@ublique/components/Loading'
import { isEmpty } from 'lodash'

import NewTranslation from 'pages/Translations/components/NewTranslation'
import Router from 'components/Router'
import ErrorBoundary from 'components/ErrorBoundary'
import isUserAuthorized from 'auth/isUserAuthorized'
import useStorageSession from 'auth/useStorageSession'
import { appConfigStore, appReadyStore, synapse } from 'services/state'
import { ExportApi } from 'services/api/export'
import { ImportApi } from 'services/api/import'
import withStore from 'hoc/withStore'
import { BASE_URL } from 'config'
import userActionMenu from './panel-actions'
import userActionAvatar from './avatar-actions'
import {
  fetchAppConfig,
  getInstancesList,
  getMyScenarioPermissions
} from './actions'
import { setConfigSidebar } from './utils'
import { isDevMode, defaultThemes, platformModals } from './constants'
import { settingsLabels } from 'components/Settings'

function App({
  routes,
  setRoutes,
  user,
  languagesMenuConfig,
  scenarioActionsConfig,
  scenarioAllowedStatusForCloning,
  scenarioWizardComponents,
  wizardComponents,
  theme,
  logo,
  sidebarIcon,
  models: clientModels,
  scenarioModelConfig: clientScenarioModel,
  outputConfigRoutes: clientOutputConfigRoutes,
  modals = {},
  themes = defaultThemes,
  outputConfigComponents,
  defaultValueGetter,
  fallbackRedirectPath,
  forceRedirectPath,
  librariesVersion,
  productChangelogUrl,
  manualsSettings
}) {
  const initialized = useStorageSession()
  const { t, i18n } = useTranslation(['Sidebar', 'Shell'])
  const { t: commonT } = useTranslation('Common')
  const { t: extraOutputT } = useTranslation('ExtraOutput')

  const { t: platT } = useTranslation([
    'ModelInfo',
    'OptionsPlatform',
    'Sidebar',
    'agGrid',
    'Translations',
    'Errors',
    'Document',
    'Models',
    'ImportLog',
    'Options'
  ])

  const checkAuthorized = useCallback(
    (route) => isUserAuthorized(user, route),
    [user]
  )

  const allModals = useMemo(() => {
    const languagesList = Object.keys(languagesMenuConfig)
    return {
      ...modals,
      ...platformModals,
      newTranslation: (props) => (
        <NewTranslation {...props} allLanguages={languagesList} />
      )
    }
  }, [modals, themes, languagesMenuConfig])

  useEffect(() => {
    const closeStream = synapse.connect(`${BASE_URL}/stream`, (e) => {
      if (!appConfigStore.get((store) => store.stream)) {
        appConfigStore.update((prevState) => ({
          ...prevState,
          stream: true
        }))
        // da inserire quando riusciremo a intercettare anche l'errore
        // addNotification({
        //   id: uniqueId('notif-'),
        //   type: 'inline',
        //   kind: 'success',
        //   title: t('Shell:StreamConnected')
        // })
        // bisogna intercettare l'errore e mandare una action di reconnect
        // saveAutomatedPanelInfo(
        //   'DELETE',
        //   {},
        //   { result: 'STREAM_CONNECTED', cta: null, errors: [] }
        // )
      }
      if (e.data === 'heartbeat' || e.data === 'connected') {
        return
      }
      synapse.publish(e.lastEventId, e)
    })

    return closeStream
  }, [])

  useEffect(async () => {
    if (initialized && !isEmpty(user)) {
      // It's needed for scenario pages loading
      appConfigStore.update((prevState) => ({
        ...prevState,
        isConfigLoading: true
      }))
      try {
        const [models, scenarioModelConfig, outputConfigModel] =
          await fetchAppConfig()
        await getInstancesList(t)
        await getMyScenarioPermissions()
        const outputConfigRoutes =
          (isDevMode && clientOutputConfigRoutes) || outputConfigModel

        appConfigStore.update((prevState) => ({
          ...prevState,
          models: (isDevMode && clientModels) || models,
          scenarioModelConfig:
            (isDevMode && clientScenarioModel) || scenarioModelConfig,
          outputConfigRoutes,
          isConfigLoading: false
        }))

        const appRoutes = setConfigSidebar(
          routes,
          outputConfigRoutes,
          scenarioModelConfig
        )

        setRoutes(appRoutes)
        appReadyStore.set(true)
      } catch (error) {
        console.warn(error)

        if (error.code !== 401) {
          appReadyStore.set(true)
        }
      }
    }
  }, [initialized, user])

  useEffect(() => {
    ExportApi.setLanguage(i18n.language)
    ImportApi.setLanguage(i18n.language)
  }, [i18n.language])

  if (!initialized) return null

  return (
    <Suspense fallback={<Loading />}>
      <ErrorBoundary>
        <Theme name={theme}>
          <Router
            routes={routes}
            manualsSettings={manualsSettings}
            settingsLabels={settingsLabels(commonT)}
            userActionMenu={userActionMenu(t)}
            userActionAvatar={userActionAvatar(t)}
            checkAuthorization={checkAuthorized}
            modals={allModals}
            extraOutputT={extraOutputT}
            // Props coming from the outside are forwarded to the router, that passes them down
            // to those components that request them (via the 'extraProps' key in the route config)
            defaultValueGetter={defaultValueGetter}
            outputConfigComponents={outputConfigComponents}
            languagesMenuConfig={languagesMenuConfig}
            scenarioActionsConfig={scenarioActionsConfig}
            wizardComponents={wizardComponents}
            scenarioWizardComponents={scenarioWizardComponents}
            scenarioAllowedStatusForCloning={scenarioAllowedStatusForCloning}
            logo={logo?.[theme]}
            sidebarIcon={sidebarIcon}
            fallbackRedirectPath={fallbackRedirectPath}
            forceRedirectPath={forceRedirectPath}
            librariesVersion={librariesVersion}
            productChangelogUrl={productChangelogUrl}
          />
        </Theme>
      </ErrorBoundary>
    </Suspense>
  )
}

export default withStore(React.memo(App), ['user', 'theme', 'appConfig'])
